import merge from 'lodash/merge';
import { createMuiTheme, responsiveFontSizes } from '@material-ui/core/styles';
import type { Direction, Theme, ThemeOptions } from '@material-ui/core';
import { THEMES } from '../constants';
import { lightShadows, darkShadows } from './shadows';

interface ThemeConfig {
  direction?: Direction;
  responsiveFontSizes?: boolean;
  roundedCorners?: boolean;
  theme?: string;
}

const baseOptions: ThemeOptions = {
  direction: 'ltr',
  components: {
    MuiAvatar: {
      styleOverrides: {
        fallback: {
          height: '75%',
          width: '75%'
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          minWidth: 100,
          '&.Mui-disabled': {
            color: 'inherit' // Keeps the text color unchanged when disabled
          }
        }
      }
    },
    MuiCardHeader: {
      defaultProps: {
        titleTypographyProps: {
          variant: 'h6'
        }
      }
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 3,
          overflow: 'hidden'
        }
      }
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 'auto',
          marginRight: '16px'
        }
      }
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          '&.Mui-checked': {
            color: '#072f40',
            '& + .MuiSwitch-track': {
              backgroundColor: '#072f40'
            }
          }
        },
        thumb: {
          '&.Mui-checked': {
            color: '#ffffff' // Optional: Ensure thumb is distinguishable
          }
        },
        track: {
          '&.Mui-checked': {
            backgroundColor: '#072f40'
          }
        }
      }
    },
    MuiTab: {
      styleOverrides: {
        root: {
          '&&.Mui-selected': {
            color: '#072f40',
            fontWeight: 'bold',
            borderBottom: '2px solid #072f40'
          },
        }
      }
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          '&.Mui-checked': {
            color: '#072f40'
          },
          '&.MuiCheckbox-colorPrimary.Mui-checked': {
            color: '#072f40'
          },
          '&.MuiCheckbox-colorSecondary.Mui-checked': {
            color: '#072f40'
          },
          '&.MuiCheckbox-indeterminate': {
            color: '#072f40'
          },
        }
      }
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          color: "#072f40",
          "&.Mui-checked": {
            color: "#072f40",
          },
        },
      },
    },
  },
  typography: {
    button: {
      fontWeight: 600
    },
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji"',
    h1: {
      fontWeight: 600,
      fontSize: '3.4375rem'
    },
    h2: {
      fontWeight: 600,
      fontSize: '2.875rem'
    },
    h3: {
      fontWeight: 600,
      fontSize: '2.125rem'
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.875rem'
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.375rem'
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem'
    },
    overline: {
      fontWeight: 600
    }
  }
};

const themesOptions: Record<string, ThemeOptions> = {
  [THEMES.LIGHT]: {
    components: {
      MuiInputBase: {
        styleOverrides: {
          input: {
            '&::placeholder': {
              opacity: 0.86,
              color: '#42526e'
            }
          }
        }
      },
      MuiTab: {
        styleOverrides: {
          root: {
            '&.Mui-selected': {
              backgroundColor: '#50b4ec',
              color: '#072f40',
              borderBottom: '2px solid #072f40'
            }
          }
        }
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            '&.Mui-focused': {
              color: '#155e96'
            },
            fontSize: 15,
          },
        }
      },
    },
    palette: {
      action: {
        active: '#6b778c'
      },
      background: {
        default: '#f4f5f7',
        paper: '#ffffff'
      },
      error: {
        contrastText: '#ffffff',
        main: '#f44336'
      },
      mode: 'light',
      primary: {
        contrastText: '#fff',
        // #155e96
        // #55b4b0
        //#9966ff
        //#ccb3ff
        //#751aff
        // main: '#563d7c',
        // main: '#234959',
        main: '#072f40',
        dark: '#33CCCC'
      },
      success: {
        contrastText: '#ffffff',
        main: 'rgb(46, 125, 50)',
      },
      secondary: {
        contrastText: 'rgb(25, 118, 210)',
        main: '#D3D3D3',
      },
      text: {
        primary: '#000',
        // primary: 'rgb(107, 119, 140)',
        secondary: '#6b778c'
      },
      warning: {
        contrastText: '#ffffff',
        main: '#ff9800'
      }
    },
    shadows: lightShadows
  },
  [THEMES.DARK]: {
    components: {
      MuiTableCell: {
        styleOverrides: {
          root: {
          }
        }
      },
      MuiTab: {
        styleOverrides: {
          root: {
            '&.Mui-selected': {
              backgroundColor: '#50b4ec',
              color: '#072f40',
              borderBottom: '2px solid #072f40'
            },
          }
        }
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            '&.Mui-focused': {
              color: '#fff',
            },
            fontSize: 15,
          },
        }
      },
    },
    palette: {
      background: {
        default: '#252630',
        paper: '#303041'
      },
      divider: 'rgba(145, 158, 171, 0.24)',
      error: {
        contrastText: '#ffffff',
        main: '#f44336'
      },
      mode: 'dark',
      primary: {
        contrastText: '#fff',
        main: '#155e96',
        dark: '#ffffff'
      },
      secondary: {
        contrastText: 'rgb(25, 118, 210)',
        main: '#D3D3D3',
      },
      success: {
        contrastText: '#ffffff',
        main: 'rgb(46, 125, 50)'
      },
      text: {
        primary: '#ffffff',
        secondary: 'rgb(128, 128, 128)'
      },
      warning: {
        contrastText: '#ffffff',
        main: '#ff9800'
      }
    },
    shadows: darkShadows
  },
  [THEMES.NATURE]: {
    components: {
      MuiTableCell: {
        styleOverrides: {
          root: {
          }
        }
      },
      MuiTab: {
        styleOverrides: {
          root: {
            '&.Mui-selected': {
              backgroundColor: '#01ab56',
            }
          }
        }
      },
    },
    palette: {
      background: {
        default: '#1c2531',
        paper: '#293142'
      },
      divider: 'rgba(145, 158, 171, 0.24)',
      error: {
        contrastText: '#ffffff',
        main: '#f44336'
      },
      mode: 'dark',
      primary: {
        contrastText: '#ffffff',
        main: '#ffffff'
      },
      success: {
        contrastText: '#ffffff',
        main: '#4caf50'
      },
      text: {
        primary: '#ffffff',
        secondary: 'rgb(128, 128, 128)'
      },
      warning: {
        contrastText: '#ffffff',
        main: '#ff9800'
      }
    },
    shadows: darkShadows
  }
};

export const createTheme = (config: ThemeConfig = {}): Theme => {
  let themeOptions = themesOptions[config.theme];

  if (!themeOptions) {
    themeOptions = themesOptions[THEMES.LIGHT];
  }

  let theme = createMuiTheme(
    merge(
      {},
      baseOptions,
      themeOptions,
      {
        ...(
          config.roundedCorners && {
            shape: {
              borderRadius: 16
            }
          }
        )
      },
      {
        direction: config.direction,
        components: {
          MuiInputBase: {
            styleOverrides: {
              input: {
                fontSize: 14,
              },
            }
          },
          MuiInputLabel: {
            styleOverrides: {
              root: {
                fontSize: 15,
              },
            }
          },
        },
      }
    )
  );

  if (config.responsiveFontSizes) {
    theme = responsiveFontSizes(theme);
  }

  return theme;
};
